import type { RecentApplication } from 'tio-common/utils/tuition-assistance/application-states';
import {
  getOutstandingRepaymentBalance,
  isInRepayment,
} from 'tio-common/utils/tuition-assistance/application-states';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

export interface AmountInfo {
  reimbursementAmount: number;
  reimbursementLabelKey: string;
  courseCashAmount: number;
  courseCashLabelKey: string;
  hasCourseCash: boolean;
}

/** Application states that are still in draft / not yet submitted */
export const REQUESTED_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

/** Application states awaiting review */
export const PENDING_STATES = new Set([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

export const getHistoryDate = (application: RecentApplication) => {
  return (
    application.tasProgramInstanceCreatedAtDate ||
    application.tasApplicationCreatedAtDate ||
    application.documentCreatedAt ||
    application.createdAt
  );
};

/**
 * Get the display name for an application
 */
export const getApplicationName = (app: RecentApplication) => {
  const type = app.tasProgramTemplateTypeKeyword;
  if (type === 'TAS.ProgramType.2' || type === 'TAS.ProgramType.3') {
    return (
      app.tasApplicationFieldApplicationNameKeyword ||
      app.tasProgramInstanceFieldProgramSpecializationKeyword ||
      `Application #${app.tasApplicationIdKeyword || app.id}`
    );
  }
  return (
    app.tasApplicationFieldApplicationNameKeyword ||
    `Application #${app.tasApplicationIdKeyword || app.id}`
  );
};

/**
 * Get the display name for a program instance
 */
export const getInstanceName = (app: RecentApplication) => {
  return (
    app.tasProgramInstanceFieldProgramSpecializationKeyword ||
    app.tasProgramInstanceFieldSchoolInstitutionNameKeyword ||
    app.tasProgramTemplateFieldProgramNameKeyword ||
    'Program'
  );
};

/**
 * Get the program template name
 */
export const getProgramName = (app: RecentApplication) => {
  return app.tasProgramTemplateFieldProgramNameKeyword || 'Unknown Program';
};

/**
 * Compute amount info for a RecentApplication (OpenSearch fields, amounts in cents).
 * Shared between getting-started and benefit-history pages.
 */
export const applicationAmountInfo = (app: RecentApplication): AmountInfo | null => {
  const state = app.tasApplicationStateKeyword || '';
  const isRequestedState = REQUESTED_STATES.has(state);

  // Reimbursement amounts (in cents → dollars)
  const approvedTotal =
    (app.tasApplicationFieldFinalApprovedTotalInteger ?? app.paymentFinalApprovedTotalAmount ?? 0) /
    100;
  const requestedTotal =
    (app.tasApplicationFieldPreApprovedTotalInteger ?? app.paymentPreApprovedTotal ?? 0) / 100;
  const paidTotal = (app.paymentPaidAmount ?? 0) / 100;

  // CourseCash amounts (in cents → dollars)
  const courseCashPreApproved = (app.tasApplicationFieldCardPreApprovedTotalInteger ?? 0) / 100;
  const courseCashFinalApproved = (app.tasApplicationFieldCardFinalApprovedTotalInteger ?? 0) / 100;
  const courseCashApproved = isRequestedState
    ? courseCashPreApproved || courseCashFinalApproved
    : courseCashFinalApproved || courseCashPreApproved;
  const courseCashUsed = (app.courseCashUsedAmount ?? 0) / 100;
  const courseCashReserved = (app.courseCashReservedAmount ?? 0) / 100;

  const isCourseCashApp = courseCashApproved > 0 || courseCashUsed > 0 || courseCashReserved > 0;

  // CourseCash display: used → reserved → approved
  let courseCashAmount = courseCashUsed;
  let courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_used';
  if (courseCashUsed <= 0 && courseCashReserved > 0) {
    courseCashAmount = courseCashReserved;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_reserved';
  } else if (courseCashUsed <= 0 && courseCashReserved <= 0 && courseCashApproved > 0) {
    courseCashAmount = courseCashApproved;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_requested';
  }

  // Refund / repayment — only show if there is an outstanding balance
  if (isInRepayment(app)) {
    const outstandingBalance = getOutstandingRepaymentBalance(app) / 100;
    return {
      reimbursementAmount: outstandingBalance,
      reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_repayment',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  // Paid
  if (paidTotal > 0) {
    return {
      reimbursementAmount: paidTotal,
      reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_paid',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  // Pending review
  if (PENDING_STATES.has(state)) {
    const amount = approvedTotal > 0 ? approvedTotal : requestedTotal;
    if (amount <= 0 && !isCourseCashApp) return null;
    return {
      reimbursementAmount: amount,
      reimbursementLabelKey:
        approvedTotal > 0
          ? 'tuition_assistance.reimagined.index.application_amount_pending'
          : 'tuition_assistance.reimagined.index.application_amount_requested',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  // Fallback: approved → requested
  const reimbursementAmount = approvedTotal > 0 ? approvedTotal : requestedTotal;
  if (reimbursementAmount <= 0 && !isCourseCashApp) return null;
  return {
    reimbursementAmount,
    reimbursementLabelKey:
      approvedTotal > 0
        ? 'tuition_assistance.reimagined.index.application_amount_approved'
        : 'tuition_assistance.reimagined.index.application_amount_requested',
    courseCashAmount,
    courseCashLabelKey,
    hasCourseCash: isCourseCashApp,
  };
};

/**
 * Compute amount info from a TasApplicationModel (Ember Data record).
 * Mirrors applicationAmountInfo() but uses model.amounts instead of OpenSearch fields.
 */
export const amountInfoFromTasApplication = (app: TasApplicationModel): AmountInfo | null => {
  const amounts = app.amounts;
  if (!amounts) return null;

  const state = app.state || '';

  const paidTotal = amounts.paid / 100;
  const approvedTotal = amounts.approved_for_payment / 100;
  const requestedTotal = amounts.requested / 100;

  const courseCashReserved = (amounts.course_cash?.reserved ?? 0) / 100;
  const courseCashUsed = (amounts.course_cash?.used ?? 0) / 100;
  const courseCashRefunded = (amounts.course_cash?.refunded ?? 0) / 100;
  const cardPreApprovedTotal = ((app.cardPreApprovedTotal as number) ?? 0) / 100;
  const cardFinalApprovedTotal = ((app.cardFinalApprovedTotal as number) ?? 0) / 100;

  const isCourseCashApp =
    courseCashUsed > 0 ||
    courseCashReserved > 0 ||
    courseCashRefunded > 0 ||
    cardPreApprovedTotal > 0 ||
    cardFinalApprovedTotal > 0;

  let courseCashAmount = 0;
  let courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_used';
  if (courseCashUsed > 0) {
    courseCashAmount = courseCashUsed;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_used';
  } else if (courseCashReserved > 0) {
    courseCashAmount = courseCashReserved;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_reserved';
  } else if (cardFinalApprovedTotal > 0) {
    courseCashAmount = cardFinalApprovedTotal;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_approved';
  } else if (cardPreApprovedTotal > 0) {
    courseCashAmount = cardPreApprovedTotal;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_pending';
  } else if (courseCashRefunded > 0) {
    courseCashAmount = courseCashRefunded;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_used';
  }

  const refundRequested = amounts.refund_requested ?? 0;
  const refundPaidBack = (amounts.refund_paid_back ?? 0) as number;
  const refundForgiven = (amounts.refund_forgiven ?? 0) as number;
  const refundCancelled = (amounts.refund_cancelled ?? 0) as number;
  const outstandingRepaymentCents =
    refundRequested - (refundPaidBack + refundForgiven + refundCancelled);

  if (amounts.flags?.request_repayment_enabled && outstandingRepaymentCents > 0) {
    return {
      reimbursementAmount: outstandingRepaymentCents / 100,
      reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_repayment',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  if (paidTotal > 0) {
    return {
      reimbursementAmount: paidTotal,
      reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_paid',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  if (PENDING_STATES.has(state)) {
    const amount = approvedTotal > 0 ? approvedTotal : requestedTotal;
    if (amount <= 0 && !isCourseCashApp) return null;
    return {
      reimbursementAmount: amount,
      reimbursementLabelKey:
        approvedTotal > 0
          ? 'tuition_assistance.reimagined.index.application_amount_pending'
          : 'tuition_assistance.reimagined.index.application_amount_requested',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  const reimbursementAmount = REQUESTED_STATES.has(state)
    ? requestedTotal || approvedTotal
    : approvedTotal || requestedTotal;
  if (reimbursementAmount <= 0 && !isCourseCashApp) return null;
  return {
    reimbursementAmount,
    reimbursementLabelKey:
      approvedTotal > 0
        ? 'tuition_assistance.reimagined.index.application_amount_approved'
        : 'tuition_assistance.reimagined.index.application_amount_requested',
    courseCashAmount,
    courseCashLabelKey,
    hasCourseCash: isCourseCashApp,
  };
};
